import { replaceMentions } from "@/components/communications-chat-components/chatMentionsUtils";
import { Colors } from "@/enums/Colors";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import { LazoChatConversationShell } from "@/models/ai-chat/LazoChatConversation";
import { LazoChatTypingDTO } from "@/models/ai-chat/LazoChatTyping";
import { Badge, badgeClasses, Box, ListItem, ListItemText, styled, Typography, TypographyProps } from "@mui/material";
import { marked } from "marked";
import moment from "moment";
import React from "react";
import read from "../../../assets/images/Read.svg";
import { ChatAvatar } from "./ChatAvatar";

type Props = {
  chat: LazoChatConversationShell;
  onChatSelect: (chat: LazoChatConversationShell) => void;
  isSelected: boolean;
  userTyping?: LazoChatTypingDTO;
};

function arePropsEqual(oldProps: Props, newProps: Props): boolean {
  return oldProps.chat === newProps.chat && oldProps.userTyping === newProps.userTyping && oldProps.isSelected === newProps.isSelected;
}

export const ChatListItem = React.memo(function ChatListItem({ chat, onChatSelect, userTyping, isSelected }: Props) {
  const currentUser = AuthService.getUser()?.externalId;

  const isToday = chat.updatedAt ? moment(chat.updatedAt || "2022-01-01").isSame(Date.now(), "day") : false;

  function renderMessage(message: string | undefined) {
    if (!message) {
      return;
    }

    return <Box dangerouslySetInnerHTML={{ __html: marked.parse(replaceMentions(message)) }} />;
  }

  return (
    <ChatListItemContainer isSelected={isSelected} onClick={() => onChatSelect(chat)}>
      <Box>
        <ChatAvatar conversation={chat} />
      </Box>

      <ListItemText
        primary={
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <ConversationTitleTypography>{chat.title}</ConversationTitleTypography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              {chat.unreadMessagesCount == 0 && <img alt="read" src={read.src} />}

              <DateTypography>{isToday ? moment(chat.updatedAt).format("HH:mm") : moment(chat.updatedAt).format("ddd")}</DateTypography>
            </Box>
          </Box>
        }
        secondary={
          <Box>
            {
              <Typography
                component="span"
                variant="body2"
                color="text.primary"
                sx={{
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                }}
              >
                {chat.tenant.name}
              </Typography>
            }

            <Content>
              <ListItemSubtext>
                {userTyping && userTyping.user.externalId !== currentUser
                  ? `${userTyping.user.firstName} is typing...`
                  : renderMessage(chat.lastMessageText)}
              </ListItemSubtext>
              {chat.unreadMessagesCount > 0 && (
                <>
                  <StyledBadge badgeContent={chat.unreadMessagesCount}></StyledBadge>
                </>
              )}
            </Content>
          </Box>
        }
        sx={{
          "& .MuiListItemText-secondary": {
            fontSize: "0.75rem",
            fontWeight: 400,
            lineHeight: "1.25rem",
            letterSpacing: "0em",
            textAlign: "left",
            color: Colors.LIGHT_GRAY_8A8E93,
          },
          "&.MuiListItemText-root": {
            margin: "0 !important",
          },
        }}
      />
    </ChatListItemContainer>
  );
}, arePropsEqual);

const ChatListItemContainer = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== "isSelected",
})<{
  isSelected: boolean;
}>(({ theme, isSelected }) => ({
  margin: "0 !important",
  gap: "1rem",
  width: "100%",
  transition: theme.transitions.create(["background-color", "box-shadow"], {
    duration: theme.transitions.duration.short,
  }),
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.1)",
  },
  backgroundColor: isSelected ? Colors.LIGHT_GRAY_BG_F6F8FA : "transparent",
  padding: "0.75rem 1.5rem",
  height: "5rem",
  cursor: "pointer",
}));

const Avatar = styled(Box)(() => ({
  width: "40px",
  height: "40px",
  borderRadius: "8px",
  border: Colors.LIGHT_GRAY_8A8E93,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
}));

const Content = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row",
  gap: "1rem",
  alignItems: "center",
  justifyContent: "flex-start",
  fontSize: ".12px",
}));

const StyledBadge = styled(Badge)(() => ({
  position: "absolute",
  right: "2.125rem",
  [`& .${badgeClasses.badge}`]: {
    minWidth: "1.25rem",
    minHeight: "1.25rem",
    fontSize: "0.625rem",
    paddingRight: "0.45rem",
    fontWeight: 700,
    color: Colors.WHITE,
    backgroundColor: Colors.DARK_GREY_3E3E3E,
  },
}));

const DateTypography = styled(Typography)(() => ({
  fontSize: "0.625rem",
  fontWeight: 400,
  color: Colors.LIGHT_GRAY_8A8E93,
}));

const ListItemSubtext = styled(({ children, ...props }: TypographyProps) => (
  <Typography children={children} component={"span"} variant="body2" color="text.secondary" {...props} />
))(() => ({
  fontSize: "0.75rem",
  display: "flex",
  fontWeight: 400,
  whiteSpace: "nowrap",
  alignItems: "center",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "90%",
  maxHeight: "1.25rem",
}));

const StatusCircle = () => (
  <span
    style={{
      position: "absolute",
      top: "0.125rem",
      right: "0.125rem",
      transform: "translate(50%, -50%)",
      height: "0.925rem",
      width: "0.925rem",
      borderRadius: "50%",
      backgroundColor: "green",
      border: "2px solid white",
    }}
  />
);

const ConversationTitleTypography = styled(({ children, ...props }: TypographyProps) => (
  <Typography children={children} component={"span"} {...props} />
))(() => ({
  fontSize: "0.875rem",
  fontWeight: 500,
  color: Colors.BLACK_151517,
}));

export default ChatListItem;
